import { useReducer, useState } from "react";
import { useLocation } from "wouter";
import type {
  AssistantFavoriteActe,
  DentistActes,
  DentistUnaryImplant,
  DentistBridgeImplant,
  CrownOnTooth,
  ToothZone,
} from "cadendar-shared";
import { toast } from "react-toastify";
import { useAssistantFavoriteActes } from "../../../components/CCAM/hooks/useAssistantFavoriteActes";

type WizardType = DentistActes["type"];
type UnaryAbutment = DentistUnaryImplant["abutment"];
type BridgeAbutment = DentistBridgeImplant["abutment"];
type Assembly = DentistUnaryImplant["assembly"];
type CrownMaterial = DentistActes["crown_material"];
type ProsthesisCore = CrownOnTooth["core"];
type TempCrown = CrownOnTooth["temp_crown"];
type StepKey =
  | "type"
  | "implant"
  | "abutment"
  | "assembly"
  | "material"
  | "endo"
  | "core"
  | "temp"
  | "toothZone"
  | "result";

interface WizardState {
  selectedType: WizardType | null;
  brand: string;
  model: string;
  unaryAbutment: UnaryAbutment | null;
  bridgeAbutment: BridgeAbutment | null;
  assembly: Assembly | null;
  crownMaterial: CrownMaterial | null;
  bridgeNumberOfUnits: number | null;
  teeth: number[];
  quoteLines: QuoteLineEntry[];
  endo: boolean | null;
  core: ProsthesisCore | null;
  tempCrown: TempCrown | null;
  toothZone: ToothZone | null;
}

type WizardAction =
  | { type: "replaceState"; value: WizardState }
  | { type: "setType"; value: WizardType }
  | { type: "setBrand"; value: string }
  | { type: "setModel"; value: string }
  | { type: "setUnaryAbutment"; value: UnaryAbutment }
  | { type: "setBridgeAbutment"; value: BridgeAbutment }
  | { type: "setAssembly"; value: Assembly }
  | { type: "setCrownMaterial"; value: CrownMaterial }
  | { type: "setBridgeNumberOfUnits"; value: number | null }
  | { type: "setTeeth"; value: number[] }
  | { type: "setQuoteLines"; value: QuoteLineEntry[] }
  | { type: "setEndo"; value: boolean }
  | { type: "setCore"; value: ProsthesisCore }
  | { type: "setTempCrown"; value: TempCrown }
  | { type: "setToothZone"; value: ToothZone }
  ;

const initialState: WizardState = {
  selectedType: null,
  brand: "",
  model: "",
  unaryAbutment: null,
  bridgeAbutment: null,
  assembly: null,
  crownMaterial: null,
  bridgeNumberOfUnits: null,
  teeth: [],
  quoteLines: [],
  endo: null,
  core: null,
  tempCrown: null,
  toothZone: null,
};

const createStateFromActe = (
  acte: DentistActes,
  quoteLines: QuoteLineEntry[] = [],
): WizardState => {
  if (acte.type === "unary_implant") {
    return {
      selectedType: acte.type,
      brand: acte.brand,
      model: acte.model,
      unaryAbutment: acte.abutment,
      bridgeAbutment: null,
      assembly: acte.assembly,
      crownMaterial: acte.crown_material,
      bridgeNumberOfUnits: null,
      teeth: [],
      quoteLines,
      endo: null,
      core: null,
      tempCrown: null,
      toothZone: null,
    };
  }

  if (acte.type === "bridge_implant") {
    return {
      selectedType: acte.type,
      brand: acte.brand,
      model: acte.model,
      unaryAbutment: null,
      bridgeAbutment: acte.abutment,
      assembly: acte.assembly,
      crownMaterial: acte.crown_material,
      bridgeNumberOfUnits: acte.number_of_units,
      teeth: [],
      quoteLines,
      endo: null,
      core: null,
      tempCrown: null,
      toothZone: null,
    };
  }

  if (acte.type === "crown_on_tooth") {
    return {
      selectedType: acte.type,
      brand: "",
      model: "",
      unaryAbutment: null,
      bridgeAbutment: null,
      assembly: null,
      crownMaterial: acte.crown_material,
      bridgeNumberOfUnits: null,
      teeth: [],
      quoteLines,
      endo: acte.endo,
      core: acte.core,
      tempCrown: acte.temp_crown,
      toothZone: acte.tooth_zone,
    };
  }

  return {
    selectedType: null,
    brand: "",
    model: "",
    unaryAbutment: null,
    bridgeAbutment: null,
    assembly: null,
    crownMaterial: null,
    bridgeNumberOfUnits: null,
    teeth: [],
    quoteLines,
    endo: null,
    core: null,
    tempCrown: null,
    toothZone: null,
  };
};

const previousBrands = [
  "Straumann",
  "Nobel Biocare",
  "Astra Tech",
  "Anthogyr",
  "Biotech Dental",
];

const previousModels = [
  "BLX",
  "Tapered Pro",
  "EV",
  "Axiom",
  "Kontact",
];

const unaryAbutmentOptions: Array<{ value: UnaryAbutment; label: string }> = [
  { value: "none", label: "Pas de pilier" },
  { value: "standard", label: "Pilier standard" },
  { value: "custom", label: "Pilier personnalise" },
];

const bridgeAbutmentOptions: Array<{ value: BridgeAbutment; label: string }> = [
  { value: "none", label: "Pas de pilier" },
  { value: "conical", label: "Pilier conique" },
  { value: "standard", label: "Pilier standard pour scellee" },
  { value: "custom", label: "Pilier personnalise pour scellee" },
  { value: "other", label: "Autre" },
];

const assemblyOptions: Array<{ value: Assembly; label: string }> = [
  { value: "cement_retained", label: "Scellee" },
  { value: "screw_retained", label: "Transvissee" },
];

const crownMaterialOptions: Array<{ value: CrownMaterial; label: string }> = [
  { value: "pfm", label: "CCM" },
  { value: "metal", label: "Metal" },
  { value: "veneered_zirconia", label: "Zircone stratifiee" },
  { value: "monolithic_zirconia", label: "Zircone monolithique" },
  { value: "other_ceramic", label: "Autre ceramique non zircone" },
];

const endoOptions: Array<{ value: boolean; label: string }> = [
  { value: true, label: "Dent devitalisee" },
  { value: false, label: "Dent pulpée" },
];

const coreOptions: Array<{ value: ProsthesisCore; label: string }> = [
  { value: "none", label: "Pas de reconstitution" },
  { value: "composite", label: "Composite" },
  { value: "composite with prefab post", label: "Tenon + composite" },
  { value: "cast metal", label: "Inlay-Core metal" },
  { value: "other laboratory core", label: "Inlay-Core autre" },
];

const tempCrownOptions: Array<{ value: TempCrown; label: string }> = [
  { value: "none", label: "Pas de provisoire" },
  { value: "direct", label: "Provisoire cabinet" },
  { value: "indirect", label: "Provisoire labo" },
];

const toothZoneOptions: Array<{ value: ToothZone; label: string }> = [
  { value: "molar", label: "Molaires" },
  { value: "second_premolar", label: "2e premolaire" },
  { value: "incisor_canine_first_premolar", label: "Incisives, canines, 1ere premolaire" },
];

const trimValue = (value: string) => value.trim();

const ALL_STEPS: StepKey[] = [
  "type",
  "implant",
  "abutment",
  "assembly",
  "material",
  "endo",
  "core",
  "temp",
  "toothZone",
  "result",
];

const getVisibleSteps = (state: WizardState): StepKey[] => {
  if (state.selectedType === null) {
    return ["type"];
  }

  if (state.selectedType !== "crown_on_tooth") {
    return ["type", "implant", "abutment", "assembly", "material", "result"];
  }

  return ["type", "endo", "core", "temp", "material", "toothZone", "result"];
};

const isStepComplete = (step: StepKey, state: WizardState) => {
  switch (step) {
    case "type":
      return state.selectedType !== null;
    case "implant":
      if (trimValue(state.brand) === "" || trimValue(state.model) === "") {
        return false;
      }
      if (state.selectedType === "unary_implant") {
        return true;
      }
      if (state.selectedType === "bridge_implant") {
        return (
          state.bridgeNumberOfUnits !== null && state.bridgeNumberOfUnits > 0
        );
      }
      return false;
    case "abutment":
      if (state.selectedType === "unary_implant") {
        return state.unaryAbutment !== null;
      }
      if (state.selectedType === "bridge_implant") {
        return state.bridgeAbutment !== null;
      }
      return false;
    case "assembly":
      return state.assembly !== null;
    case "material":
      return state.crownMaterial !== null;
    case "endo":
      return state.endo !== null;
    case "core":
      return state.core !== null;
    case "temp":
      return state.tempCrown !== null;
    case "toothZone":
      if (state.selectedType === "crown_on_tooth") {
        return state.toothZone !== null;
      }
      return true;
    case "result":
      return buildResult(state) !== null;
  }
};

const isStepAccessible = (step: StepKey, state: WizardState): boolean => {
  switch (step) {
    case "type":
      return true;
    case "implant":
      return state.selectedType !== null && state.selectedType !== "crown_on_tooth";
    case "abutment":
      if (state.selectedType === "crown_on_tooth") {
        return false;
      }
      if (trimValue(state.brand) === "" || trimValue(state.model) === "") {
        return false;
      }
      if (state.selectedType === "bridge_implant") {
        return state.bridgeNumberOfUnits !== null && state.bridgeNumberOfUnits > 0;
      }
      return true;
    case "assembly":
      if (state.selectedType === "crown_on_tooth") {
        return false;
      }
      if (state.selectedType === "unary_implant") {
        return state.unaryAbutment !== null;
      }
      if (state.selectedType === "bridge_implant") {
        return state.bridgeAbutment !== null;
      }
      return false;
    case "material":
      if (state.selectedType === "crown_on_tooth") {
        return state.tempCrown !== null;
      }
      return state.assembly !== null;
    case "endo":
      return state.selectedType === "crown_on_tooth";
    case "core":
      if (state.selectedType !== "crown_on_tooth") {
        return false;
      }
      return state.endo !== null;
    case "temp":
      if (state.selectedType !== "crown_on_tooth") {
        return false;
      }
      return state.core !== null;
    case "toothZone":
      if (state.selectedType !== "crown_on_tooth") {
        return false;
      }
      return state.crownMaterial !== null;
    case "result":
      if (state.selectedType === "crown_on_tooth") {
        return state.toothZone !== null;
      }
      return state.crownMaterial !== null;
  }
};

const reducer = (state: WizardState, action: WizardAction): WizardState => {
  switch (action.type) {
    case "replaceState":
      return action.value;
    case "setType":
      return {
        ...state,
        selectedType: action.value,
        unaryAbutment: null,
        bridgeAbutment: null,
        assembly: null,
        crownMaterial: null,
        endo: null,
        core: null,
        tempCrown: null,
        toothZone: null,
      };
    case "setBrand":
      return { ...state, brand: action.value };
    case "setModel":
      return { ...state, model: action.value };
    case "setUnaryAbutment":
      return { ...state, unaryAbutment: action.value };
    case "setBridgeAbutment":
      return { ...state, bridgeAbutment: action.value };
    case "setAssembly":
      return { ...state, assembly: action.value };
    case "setCrownMaterial":
      return { ...state, crownMaterial: action.value };
    case "setBridgeNumberOfUnits":
      return { ...state, bridgeNumberOfUnits: action.value };
    case "setTeeth":
      return { ...state, teeth: action.value };
    case "setQuoteLines":
      return { ...state, quoteLines: action.value };
    case "setEndo":
      return { ...state, endo: action.value };
    case "setCore":
      return { ...state, core: action.value };
    case "setTempCrown":
      return { ...state, tempCrown: action.value };
    case "setToothZone":
      return { ...state, toothZone: action.value };
  }
};

const buildResult = (state: WizardState): DentistActes | null => {
  const brand = trimValue(state.brand);
  const model = trimValue(state.model);

  if (state.selectedType === "unary_implant") {
    if (
      brand === "" ||
      model === "" ||
      state.unaryAbutment === null ||
      state.assembly === null ||
      state.crownMaterial === null
    ) {
      return null;
    }

    return {
      root: "implantology",
      type: "unary_implant",
      teeth_number_type: "unary",
      brand,
      model,
      abutment: state.unaryAbutment,
      assembly: state.assembly,
      crown_material: state.crownMaterial,
    };
  }

  if (state.selectedType === "bridge_implant") {
    if (
      brand === "" ||
      model === "" ||
      state.bridgeAbutment === null ||
      state.assembly === null ||
      state.crownMaterial === null ||
      state.bridgeNumberOfUnits === null ||
      state.bridgeNumberOfUnits <= 0
    ) {
      return null;
    }

    return {
      root: "implantology",
      type: "bridge_implant",
      teeth_number_type: "multiple",
      brand,
      model,
      abutment: state.bridgeAbutment,
      assembly: state.assembly,
      crown_material: state.crownMaterial,
      number_of_units: state.bridgeNumberOfUnits,
    };
  }

  if (state.selectedType === "crown_on_tooth") {
    if (
      state.endo === null ||
      state.core === null ||
      state.tempCrown === null ||
      state.crownMaterial === null ||
      state.toothZone === null
    ) {
      return null;
    }

    return {
      root: "prosthodontics",
      type: "crown_on_tooth",
      teeth_number_type: "unary",
      endo: state.endo,
      core: state.core,
      temp_crown: state.tempCrown,
      tooth_zone: state.toothZone,
      crown_material: state.crownMaterial,
    };
  }

  return null;
};

const parsePositiveInteger = (value: string) => {
  if (value.trim() === "") {
    return null;
  }

  const parsedValue = Number.parseInt(value, 10);
  if (Number.isNaN(parsedValue) || parsedValue <= 0) {
    return null;
  }

  return parsedValue;
};

const IMPLANT_CCAM: Record<number, string> = {
  1: "LBLD015",
  2: "LBLD010",
  3: "LBLD013",
  4: "LBLD004",
  5: "LBLD020",
  6: "LBLD025",
  7: "LBLD026",
  8: "LBLD038",
  9: "LBLD200",
  10: "LBLD294",
};

const ABUTMENT_CCAM: Record<number, string> = {
  1: "HBLD012",
  2: "HBLD017",
  3: "HBLD021",
  4: "HBLD013",
  5: "HBLD005",
};

const CROWN_CCAM = "HBLD418";

type Basket = "rac0" | "libre" | "modere";

const getProsthesisBasket = (
  material: CrownMaterial,
  zone: ToothZone,
): Basket => {
  if (material === "veneered_zirconia") {
    return "libre";
  }
  if (material === "metal") {
    return "rac0";
  }
  if (material === "monolithic_zirconia") {
    return "rac0";
  }
  if (material === "pfm") {
    if (zone === "molar") {
      return "libre";
    }
    if (zone === "second_premolar") {
      return "modere";
    }
    return "rac0";
  }
  if (material === "other_ceramic") {
    if (zone === "molar" || zone === "second_premolar") {
      return "modere";
    }
    return "rac0";
  }
  return "libre";
};

const getInlayCoreCode = (basket: Basket): string => {
  if (basket === "rac0") return "HBLD090";
  if (basket === "modere") return "HBLD745";
  return "HBLD245";
};

const getProvisionalCode = (basket: Basket): string => {
  if (basket === "rac0") return "HBLD490";
  if (basket === "modere") return "HBLD724";
  return "HBLD486";
};

const getCompositeCode = (
  core: "composite" | "composite with prefab post",
  zone: ToothZone,
): string => {
  if (core === "composite with prefab post") {
    return "HBMD042";
  }
  if (zone === "incisor_canine_first_premolar") {
    return "HBMD054";
  }
return "HBMD038";
};

const getCrownCode = (
  material: CrownMaterial,
  zone: ToothZone,
): string => {
  if (material === "veneered_zirconia") {
    return "HBLD403";
  }
  if (material === "metal") {
    return "HBLD038";
  }
  if (material === "monolithic_zirconia") {
    if (zone === "molar") return "HBLD073";
    return "HBLD350";
  }
  if (material === "pfm") {
    if (zone === "molar") return "HBLD734";
    if (zone === "second_premolar") return "HBLD491";
    return "HBLD634";
  }
  if (material === "other_ceramic") {
    if (zone === "molar" || zone === "second_premolar") return "HBLD158";
    return "HBLD680";
  }
  return "HBLD403";
};

interface QuoteLineEntry {
  description: string;
  ccam_code: string;
  teeth: number[];
  fees: string;
}

const buildQuoteLines = (state: WizardState): QuoteLineEntry[] => {
  if (state.quoteLines.length > 0) {
    return state.quoteLines;
  }

  const lines: QuoteLineEntry[] = [];

  if (state.selectedType === "crown_on_tooth") {
    if (
      state.crownMaterial === null ||
      state.toothZone === null ||
      state.core === null ||
      state.tempCrown === null
    ) {
      return lines;
    }

    const basket = getProsthesisBasket(state.crownMaterial, state.toothZone);

    if (state.core === "composite" || state.core === "composite with prefab post") {
      const compositeLabel =
        state.core === "composite" ? "Reconstitution composite" : "Tenon + reconstitution composite";
      lines.push({
        description: compositeLabel,
        ccam_code: getCompositeCode(state.core, state.toothZone),
        teeth: [],
        fees: "",
      });
    } else if (state.core === "cast metal" || state.core === "other laboratory core") {
      const coreLabel =
        state.core === "cast metal" ? "Inlay-Core metal" : "Inlay-Core autre";
      lines.push({
        description: coreLabel,
        ccam_code: getInlayCoreCode(basket),
        teeth: [],
        fees: "",
      });
    }

    const materialLabel =
      crownMaterialOptions.find((m) => m.value === state.crownMaterial)?.label ??
      state.crownMaterial ??
      "";
    const crownDescription =
      state.toothZone === "all"
        ? `Couronne ${materialLabel}`
        : `Couronne ${materialLabel} - ${state.toothZone}`;
    lines.push({
      description: crownDescription,
      ccam_code: getCrownCode(state.crownMaterial, state.toothZone),
      teeth: [],
      fees: "",
    });

    if (state.tempCrown !== "none") {
      const tempLabel =
        state.tempCrown === "direct" ? "Cabinet" : "Labo";
      lines.push({
        description: `Provisoire ${tempLabel}`,
        ccam_code: getProvisionalCode(basket),
        teeth: [],
        fees: "",
      });
    }

    return lines;
  }

  const n =
    state.selectedType === "unary_implant" ? 1 : (state.bridgeNumberOfUnits ?? 0);

  if (n === 0) return lines;

  lines.push({
    description: `Implant ${state.brand} ${state.model}`,
    ccam_code: IMPLANT_CCAM[n] ?? "LBLD261",
    teeth: state.teeth.slice(0, n),
    fees: "",
  });

  const abutment =
    state.selectedType === "unary_implant"
      ? state.unaryAbutment
      : state.bridgeAbutment;
  if (abutment !== null && abutment !== "none") {
    lines.push({
      description: `Pilier ${abutment}`,
      ccam_code: ABUTMENT_CCAM[n] ?? "HBLD005",
      teeth: state.teeth.slice(0, n),
      fees: "",
    });
  }

  const crownLabel =
    state.selectedType === "unary_implant" ? "Couronne" : `Bridge ${n} unites`;
  const materialLabel =
    crownMaterialOptions.find((m) => m.value === state.crownMaterial)?.label ??
    state.crownMaterial ??
    "";
  lines.push({
    description: `${crownLabel} ${materialLabel}`,
    ccam_code: CROWN_CCAM,
    teeth: state.teeth.slice(0, n),
    fees: "",
  });

  return lines;
};

const OptionCards = <TValue extends string | boolean>({
  options,
  selectedValue,
  onSelect,
}: {
  options: Array<{ value: TValue; label: string }>;
  selectedValue: TValue | null;
  onSelect: (value: TValue) => void;
}) => {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {options.map((option) => {
        const isSelected = selectedValue === option.value;

        return (
          <button
            key={String(option.value)}
            type="button"
            onClick={() => onSelect(option.value)}
            className={[
              "rounded-xl border p-4 text-left transition-all",
              isSelected
                ? "border-blue-600 bg-blue-50 text-blue-950 shadow-sm"
                : "border-slate-200 bg-white text-slate-900 hover:border-slate-400",
            ].join(" ")}
          >
            <div className="text-sm font-medium">{option.label}</div>
          </button>
        );
      })}
    </div>
  );
};

const SummaryLine = ({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2 text-sm last:border-b-0">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-medium text-slate-900">
      {value === null || value === "" ? "-" : value}
    </span>
  </div>
);

const TeethInput = ({
  teeth,
  onChange,
  maxTeeth,
}: {
  teeth: number[];
  onChange: (teeth: number[]) => void;
  maxTeeth: number;
}) => {
  const [inputValue, setInputValue] = useState("");

  const handleAdd = () => {
    const num = parsePositiveInteger(inputValue);
    if (num !== null && num > 0 && num <= 99 && !teeth.includes(num)) {
      onChange([...teeth, num].sort((a, b) => a - b));
      setInputValue("");
    }
  };

  const handleRemove = (tooth: number) => {
    onChange(teeth.filter((t) => t !== tooth));
  };

  return (
    <div className="space-y-2">
      <span className="block text-sm font-medium text-slate-700">Numero(s) de dent(s)</span>
      <div className="flex flex-wrap gap-2">
        {teeth.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800"
          >
            {t}
            <button
              type="button"
              onClick={() => handleRemove(t)}
              className="ml-1 rounded-full bg-blue-200 px-1.5 text-blue-900 hover:bg-blue-300"
            >
              x
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          inputMode="numeric"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          className="w-20 rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-blue-600"
          placeholder="Ex: 46"
        />
        <button
          type="button"
          onClick={handleAdd}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-500"
        >
          Ajouter
        </button>
      </div>
      <p className="text-xs text-slate-500">
        Pour un bridge, indiquez les {maxTeeth} numeros de dents concernes.
      </p>
    </div>
  );
};

const QuoteLinesPanel = ({
  lines,
  onLineChange,
}: {
  lines: QuoteLineEntry[];
  onLineChange: (index: number, field: "description" | "fees", value: string) => void;
}) => {
  const isValidFees = (value: string) => {
    if (value.trim() === "") return false;
    const num = parseFloat(value.replace(",", "."));
    return !isNaN(num) && num >= 0;
  };

  if (lines.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm text-slate-500">Aucune ligne de devis disponible.</p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-900">Lignes de devis generees</h3>
      <div className="overflow-hidden rounded-xl border border-slate-200">
        <table className="w-full table-auto text-sm">
          <thead className="bg-slate-100 text-left">
            <tr>
              <th className="px-3 py-2 font-medium text-slate-700">Description</th>
              <th className="px-3 py-2 font-medium text-slate-700">Code CCAM</th>
              <th className="px-3 py-2 font-medium text-slate-700">Dents</th>
              <th className="px-3 py-2 font-medium text-slate-700">Honoraires</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {lines.map((line, idx) => (
              <tr key={idx} className="text-slate-700">
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={line.description}
                    onChange={(e) => onLineChange(idx, "description", e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-slate-900 outline-none transition focus:border-blue-600"
                  />
                </td>
                <td className="px-3 py-2 font-mono text-slate-600">{line.ccam_code}</td>
                <td className="px-3 py-2">{line.teeth.length > 0 ? line.teeth.join(", ") : "-"}</td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={line.fees}
                    onChange={(e) => onLineChange(idx, "fees", e.target.value)}
                    placeholder="0.00"
                    className={[
                      "w-24 rounded-lg border bg-white px-2 py-1 outline-none transition",
                      isValidFees(line.fees) || line.fees.trim() === ""
                        ? "border-slate-300 text-slate-900 focus:border-blue-600"
                        : "border-red-300 text-red-900 focus:border-red-600",
                    ].join(" ")}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ActesWizardTestPage = () => {
  const [, navigate] = useLocation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [favoriteLabel, setFavoriteLabel] = useState("");
  const [editingFavoriteId, setEditingFavoriteId] = useState<string | null>(null);
  const [saveFeedback, setSaveFeedback] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const assistantFavorites = useAssistantFavoriteActes();

  const visibleSteps = getVisibleSteps(state);
  const result = buildResult(state);
  const quoteLines = buildQuoteLines(state);

  const handleLineChange = (index: number, field: "description" | "fees", value: string) => {
    const updated = [...quoteLines];
    updated[index] = { ...updated[index], [field]: value };
    dispatch({ type: "setQuoteLines", value: updated });
    setIsDirty(true);
  };

  const handleSaveFavorite = async () => {
    if (result === null) {
      const text = "Complétez le wizard avant d'enregistrer.";
      setSaveFeedback({ type: "error", text });
      toast.error(text);
      return;
    }

    const trimmedLabel = favoriteLabel.trim();
    if (trimmedLabel === "") {
      const text = "Donnez un nom à ce favori.";
      setSaveFeedback({ type: "error", text });
      toast.error(text);
      return;
    }

    try {
      const savedFavorite = await assistantFavorites.upsertFavorite({
        id: editingFavoriteId ?? undefined,
        label: trimmedLabel,
        acte: result,
        quoteLines: quoteLines,
      });
      const text =
        editingFavoriteId === null
          ? "Favori assistant enregistré."
          : "Favori assistant modifié.";

      setSaveFeedback({ type: "success", text });
      toast.success(text);
      setFavoriteLabel(savedFavorite.label);
      setEditingFavoriteId(savedFavorite.id);
      setIsDirty(false);
    } catch (error) {
      const text =
        error instanceof Error
          ? error.message
          : "Impossible d'enregistrer le favori assistant.";
      setSaveFeedback({ type: "error", text });
      toast.error(text);
    }
  };

  const handleEditFavorite = (favorite: AssistantFavoriteActe) => {
    dispatch({
      type: "replaceState",
      value: createStateFromActe(favorite.acte, favorite.quoteLines),
    });
    setFavoriteLabel(favorite.label);
    setEditingFavoriteId(favorite.id);
    setSaveFeedback(null);
    setIsDirty(false);
  };

  const handleDeleteFavorite = async (favorite: AssistantFavoriteActe) => {
    try {
      await assistantFavorites.deleteFavorite({ id: favorite.id });

      if (editingFavoriteId === favorite.id) {
        setEditingFavoriteId(null);
        setFavoriteLabel("");
        setIsDirty(false);
      }

      setSaveFeedback({ type: "success", text: "Favori assistant supprimé." });
      toast.success("Favori assistant supprimé.");
    } catch (error) {
      const text =
        error instanceof Error
          ? error.message
          : "Impossible de supprimer le favori assistant.";
      setSaveFeedback({ type: "error", text });
      toast.error(text);
    }
  };

  const handleNewFavorite = () => {
    if (isDirty) {
      if (!window.confirm("Des données ont été saisies. Voulez-vous vraiment les effacer pour créer un nouveau favori ?")) {
        return;
      }
    }
    dispatch({ type: "replaceState", value: initialState });
    setFavoriteLabel("");
    setEditingFavoriteId(null);
    setSaveFeedback(null);
    setIsDirty(false);
  };

  const handleAbandon = () => {
    dispatch({ type: "replaceState", value: initialState });
    setFavoriteLabel("");
    setEditingFavoriteId(null);
    setSaveFeedback(null);
    setIsDirty(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8 text-slate-900">
      <div className="mx-auto flex max-w-7xl flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-6 py-4 shadow-sm ring-1 ring-slate-200">
          <div>
            <p className="text-sm font-medium uppercase tracking-wide text-blue-700">
              Wizard actes
            </p>
            <h1 className="text-2xl font-semibold text-slate-950">
              Saisie guidee des actes assistee
            </h1>
          </div>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-500"
          >
            Retour agenda
          </button>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_380px]">
          <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <div className="mb-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleNewFavorite}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-500"
              >
                Nouveau favori
              </button>
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {visibleSteps.map((step) => {
                const isComplete = isStepComplete(step, state);
                const isAccessible = isStepAccessible(step, state);

                return (
                  <div
                    key={step}
                    className={[
                      "rounded-full px-3 py-1 text-sm font-medium",
                      isComplete
                        ? "bg-emerald-100 text-emerald-800"
                        : isAccessible
                          ? "bg-blue-100 text-blue-800"
                          : "bg-slate-100 text-slate-400",
                    ].join(" ")}
                  >
                    {step}
                  </div>
                );
              })}
            </div>

            <div className="space-y-8">
              <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <h2 className="text-xl font-semibold text-slate-950">Type d&apos;acte</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Selectionnez le type d&apos;acte a configurer.
                  </p>
                </div>

                <OptionCards
                  options={[
                    { value: "unary_implant", label: "Implant unitaire" },
                    { value: "bridge_implant", label: "Bridge sur implants" },
                    { value: "crown_on_tooth", label: "Couronne unitaire sur dent" },
                  ]}
                  selectedValue={state.selectedType}
                  onSelect={(value) => {
                      dispatch({ type: "setType", value });
                      setIsDirty(true);
                    }}
                />
              </section>

              {state.selectedType === "crown_on_tooth" && (
                <>
                  <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-950">Etat de la dent</h2>
                    </div>

                    <OptionCards
                      options={endoOptions}
                      selectedValue={state.endo}
                      onSelect={(value) => {
                        dispatch({ type: "setEndo", value });
                        setIsDirty(true);
                      }}
                    />
                  </section>

                  {state.endo !== null && (
                    <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div>
                        <h2 className="text-xl font-semibold text-slate-950">Reconstitution</h2>
                      </div>

                      <OptionCards
                        options={coreOptions}
                        selectedValue={state.core}
                        onSelect={(value) => {
                          dispatch({ type: "setCore", value });
                          setIsDirty(true);
                        }}
                      />
                    </section>
                  )}

                  {state.core !== null && (
                    <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div>
                        <h2 className="text-xl font-semibold text-slate-950">Provisoire</h2>
                      </div>

                      <OptionCards
                        options={tempCrownOptions}
                        selectedValue={state.tempCrown}
                        onSelect={(value) => {
                          dispatch({ type: "setTempCrown", value });
                          setIsDirty(true);
                        }}
                      />
                    </section>
                  )}
                </>
              )}

              {state.selectedType !== null && state.selectedType !== "crown_on_tooth" && (
                <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-950">
                      {state.selectedType === "unary_implant"
                        ? "Pose de l'implant"
                        : "Pose des implants"}
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Saisissez la marque au clavier ou cliquez sur une marque deja rencontree.
                    </p>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <label className="space-y-2">
                      <span className="block text-sm font-medium text-slate-700">Marque</span>
                      <input
                        list="implant-brand-suggestions"
                        value={state.brand}
onChange={(event) => {
                      dispatch({ type: "setBrand", value: event.target.value });
                      setIsDirty(true);
                    }}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-blue-600"
                        placeholder="Ex: Straumann"
                      />
                      <datalist id="implant-brand-suggestions">
                        {previousBrands.map((brand) => (
                          <option key={brand} value={brand} />
                        ))}
                      </datalist>
                    </label>

                    <label className="space-y-2">
                      <span className="block text-sm font-medium text-slate-700">Modele</span>
                      <input
                        list="implant-model-suggestions"
                        value={state.model}
onChange={(event) => {
                      dispatch({ type: "setModel", value: event.target.value });
                      setIsDirty(true);
                    }}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-blue-600"
                        placeholder="Ex: BLX"
                      />
                      <datalist id="implant-model-suggestions">
                        {previousModels.map((model) => (
                          <option key={model} value={model} />
                        ))}
                      </datalist>
                    </label>
                  </div>

                  {state.selectedType === "bridge_implant" ? (
                    <label className="space-y-2">
                      <span className="block text-sm font-medium text-slate-700">
                        Nombre d&apos;unites du bridge
                      </span>
                      <input
                        inputMode="numeric"
                        value={state.bridgeNumberOfUnits ?? ""}
                        onChange={(event) => {
                          dispatch({
                            type: "setBridgeNumberOfUnits",
                            value: parsePositiveInteger(event.target.value),
                          });
                          setIsDirty(true);
                        }}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-blue-600"
                        placeholder="3"
                      />
                    </label>
                  ) : null}

                </section>
              )}

              {state.selectedType !== null && trimValue(state.brand) !== "" && trimValue(state.model) !== "" && (
                <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-950">Utilisation d&apos;un pilier</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Choisissez le type de pilier correspondant a l&apos;acte.
                    </p>
                  </div>

                  {state.selectedType === "unary_implant" ? (
                    <OptionCards
                      options={unaryAbutmentOptions}
                      selectedValue={state.unaryAbutment}
                      onSelect={(value) => {
                        dispatch({ type: "setUnaryAbutment", value });
                        setIsDirty(true);
                      }}
                    />
                  ) : (
                    <OptionCards
                      options={bridgeAbutmentOptions}
                      selectedValue={state.bridgeAbutment}
                      onSelect={(value) => {
                        dispatch({ type: "setBridgeAbutment", value });
                        setIsDirty(true);
                      }}
                    />
                  )}
                </section>
              )}

              {state.selectedType !== null && (
                (state.selectedType === "unary_implant" && state.unaryAbutment !== null) ||
                (state.selectedType === "bridge_implant" && state.bridgeAbutment !== null)
              ) && (
                <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-950">
                      {state.selectedType === "unary_implant"
                        ? "Couronne scellee ou transvissee"
                        : "Bridge scelle ou transvisse"}
                    </h2>
                  </div>

                  <OptionCards
                    options={assemblyOptions}
                    selectedValue={state.assembly}
                    onSelect={(value) => {
                      dispatch({ type: "setAssembly", value });
                      setIsDirty(true);
                    }}
                  />
                </section>
              )}

              {state.assembly !== null && state.selectedType !== "crown_on_tooth" && (
                <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-950">
                      {state.selectedType === "unary_implant"
                        ? "Materiau de la couronne"
                        : "Materiau du bridge"}
                    </h2>
                  </div>

                  <OptionCards
                    options={crownMaterialOptions}
                    selectedValue={state.crownMaterial}
                    onSelect={(value) => {
                      dispatch({ type: "setCrownMaterial", value });
                      setIsDirty(true);
                    }}
                  />
                </section>
              )}

              {state.tempCrown !== null && state.selectedType === "crown_on_tooth" && (
                <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-950">Materiau de la couronne</h2>
                  </div>

                  <OptionCards
                    options={crownMaterialOptions}
                    selectedValue={state.crownMaterial}
                    onSelect={(value) => {
                      dispatch({ type: "setCrownMaterial", value });
                      dispatch({ type: "setQuoteLines", value: [] });
                      if (value === "veneered_zirconia" || value === "metal") {
                        dispatch({ type: "setToothZone", value: "all" });
                      }
                      setIsDirty(true);
                    }}
                  />
                </section>
              )}

              {state.crownMaterial !== null && state.selectedType === "crown_on_tooth" && (
                <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-950">Zone de dents</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      {state.crownMaterial === "veneered_zirconia" || state.crownMaterial === "metal"
                        ? "Cette selection est automatique pour ce materiau."
                        : "Selectionnez la zone de dents concernee."}
                    </p>
                  </div>

                  {state.crownMaterial === "veneered_zirconia" || state.crownMaterial === "metal" ? (
                    <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
                      Zone automatique : Toutes dents
                    </div>
                  ) : (
                    <OptionCards
                      options={toothZoneOptions}
                      selectedValue={state.toothZone}
                      onSelect={(value) => {
                        dispatch({ type: "setToothZone", value });
                        setIsDirty(true);
                      }}
                    />
                  )}
                </section>
              )}

              {(state.selectedType === "crown_on_tooth" ? state.toothZone !== null : state.crownMaterial !== null) && (
                <section className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-950">Objet genere</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Le JSON ci-dessous est aligne sur `actes_schema.ts`. Les reponses supplementaires du questionnaire restent visibles dans le recapitulatif a droite.
                    </p>
                  </div>

                  <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4">
                    <label className="space-y-2">
                      <span className="block text-sm font-medium text-slate-700">
                        Nom du favori
                      </span>
                      <input
                        value={favoriteLabel}
                        onChange={(event) => setFavoriteLabel(event.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-blue-600"
                        placeholder="Ex: Implant unitaire zircone Straumann"
                      />
                    </label>
                  </div>

                  {editingFavoriteId !== null ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      Mode edition actif.
                    </div>
                  ) : null}

                  {saveFeedback ? (
                    <div
                      className={[
                        "rounded-lg px-3 py-2 text-sm",
                        saveFeedback.type === "success"
                          ? "border border-emerald-200 bg-emerald-50 text-emerald-900"
                          : "border border-red-200 bg-red-50 text-red-900",
                      ].join(" ")}
                    >
                      {saveFeedback.text}
                    </div>
                  ) : null}

                  <pre className="overflow-x-auto rounded-xl bg-slate-950 p-4 text-sm text-slate-100">
                    {result === null
                      ? "Le wizard est incomplet."
                      : JSON.stringify(result, null, 2)}
                  </pre>

                  <QuoteLinesPanel
                    lines={quoteLines}
                    onLineChange={handleLineChange}
                  />
                </section>
              )}
            </div>

            <div className="mt-8 flex justify-end gap-3 border-t border-slate-200 pt-6">
              <button
                type="button"
                onClick={handleAbandon}
                className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:border-red-400"
              >
                Abandonner
              </button>
              <button
                type="button"
                onClick={handleSaveFavorite}
                disabled={result === null || assistantFavorites.isSaving}
                className="rounded-lg border border-emerald-800 bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-500"
              >
                {assistantFavorites.isSaving
                  ? "Enregistrement..."
                  : editingFavoriteId === null
                    ? "Enregistrer comme favori"
                    : "Enregistrer les modifications"}
              </button>
            </div>
          </section>

          <aside className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h2 className="text-lg font-semibold text-slate-950">Recapitulatif</h2>
            <div className="mt-4">
              <SummaryLine
                label="Type"
                value={
                  state.selectedType === "unary_implant"
                    ? "Implant unitaire"
                    : state.selectedType === "bridge_implant"
                      ? "Bridge sur implants"
                      : state.selectedType === "crown_on_tooth"
                        ? "Couronne sur dent"
                        : null
                }
              />
              {state.selectedType !== "crown_on_tooth" && (
                <>
                  <SummaryLine label="Marque" value={trimValue(state.brand) || null} />
                  <SummaryLine label="Modele" value={trimValue(state.model) || null} />
                  <SummaryLine
                    label="Nombre d'unites"
                    value={
                      state.selectedType === "bridge_implant" &&
                      state.bridgeNumberOfUnits !== null
                        ? String(state.bridgeNumberOfUnits)
                        : null
                    }
                  />
                  <SummaryLine
                    label="Pilier"
                    value={
                      state.selectedType === "unary_implant"
                        ? state.unaryAbutment
                        : state.selectedType === "bridge_implant"
                          ? state.bridgeAbutment
                          : null
                    }
                  />
                  <SummaryLine label="Assemblage" value={state.assembly} />
                </>
              )}
              {state.selectedType === "crown_on_tooth" && (
                <>
                  <SummaryLine
                    label="Etat dent"
                    value={state.endo === true ? "Devitalisee" : state.endo === false ? "Pulpée" : null}
                  />
                  <SummaryLine label="Reconstitution" value={state.core} />
                  <SummaryLine
                    label="Provisoire"
                    value={
                      state.tempCrown === "direct"
                        ? "Cabinet"
                        : state.tempCrown === "indirect"
                          ? "Labo"
                          : state.tempCrown === "none"
                            ? "Aucun"
                            : null
                    }
                  />
                </>
              )}
              <SummaryLine label="Materiau" value={state.crownMaterial} />
              {state.selectedType === "crown_on_tooth" && (
                <SummaryLine
                  label="Zone dents"
                  value={
                    state.toothZone === "all"
                      ? "Toutes"
                      : state.toothZone === "molar"
                        ? "Molaires"
                        : state.toothZone === "second_premolar"
                          ? "2e premolaire"
                          : state.toothZone === "incisor_canine_first_premolar"
                            ? "Incisives, canines, 1ere premolaire"
                            : null
                  }
                />
              )}
              <SummaryLine label="Root" value={result?.root ?? null} />
            </div>

            <div className="mt-6 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
              Les favoris enregistres sont maintenant stockes pour le proprietaire courant.
            </div>

            {assistantFavorites.mutationError ? (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                {assistantFavorites.mutationError.message}
              </div>
            ) : null}

            <div className="mt-4 rounded-xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900">
                Favoris assistant enregistres
              </h3>

              {assistantFavorites.isLoading ? (
                <p className="mt-3 text-sm text-slate-500">Chargement...</p>
              ) : assistantFavorites.favorites.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">
                  Aucun favori assistant enregistre.
                </p>
              ) : (
                <div className="mt-3 space-y-2">
                  {assistantFavorites.favorites.map((favorite) => (
                    <div
                      key={favorite.id}
                      className="rounded-lg bg-slate-50 px-3 py-2 text-sm"
                    >
                      <div className="font-medium text-slate-900">{favorite.label}</div>
                      <div className="text-slate-500">
                        {favorite.acte.type === "crown_on_tooth"
                          ? `Couronne sur dent · ${favorite.acte.crown_material}`
                          : `${favorite.acte.type} · ${favorite.acte.brand} · ${favorite.acte.model}`}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditFavorite(favorite)}
                          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:border-slate-500"
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteFavorite(favorite)}
                          disabled={assistantFavorites.isDeleting}
                          className="rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:border-red-400 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ActesWizardTestPage;
